'use client';

import { useMemo, useState } from 'react';

import type { ActiveSubscription } from '@ps/models/lib/creator-subscription';
import {
  PopStoreExtendedSubscriptionLevel,
  PopStoreExtendedUserAccessLevel,
} from '@ps/models/lib/creator-subscription';

import { cn } from '../utils';
import { ConfirmDialog } from './ConfirmDialog';

export type SubscriptionLevelBase =
  | PopStoreExtendedSubscriptionLevel
  | PopStoreExtendedUserAccessLevel;

export interface SubscriptionLevelOption<T extends SubscriptionLevelBase> {
  value: T;
  title: string;
  subTitle?: string;
  /** Force-disable the option even if the picker would otherwise enable it. */
  disabled?: boolean;
  /** Visual variant for the subtitle — `danger` = red (used for "(Inactive)"). */
  subTitleVariant?: 'default' | 'danger';
}

export interface ConfigureSubscriptionsParams {
  defaultSubscriptionLevel: PopStoreExtendedSubscriptionLevel;
  onDone: () => void;
}

export type ConfigureSubscriptions = (
  params: ConfigureSubscriptionsParams,
) => void;

interface CommonProps<T extends SubscriptionLevelBase> {
  options: SubscriptionLevelOption<T>[];
  /** Configured tiers — drives the "is this tier configured?" check. */
  activeSubscriptions?: ActiveSubscription[];
  /**
   * If provided, selecting an unconfigured non-FOLLOWER tier shows a
   * "Subscription not configured — configure now?" confirm. If
   * omitted, unconfigured tiers are simply rendered disabled
   * (`onConfigureSubscriptions` is the explicit CTA toggle).
   */
  onConfigureSubscriptions?: ConfigureSubscriptions;
  /**
   * Re-fetches the active subscriptions after a configure round-trip.
   * Called with a callback that fires once the refresh completes so
   * the picker can decide whether to commit the value.
   */
  refreshSubscriptions?: (options?: {
    callback?: (
      error: unknown | undefined,
      subscriptionList: { platform_sub_level: string }[],
    ) => void;
  }) => Promise<void> | void;
  disabled?: boolean;
  className?: string;
  itemClassName?: string;
}

type Props<T extends SubscriptionLevelBase> = CommonProps<T> &
  (
    | {
        multiple?: false;
        value?: T;
        onChange?: (level: T) => void;
      }
    | {
        multiple: true;
        value?: T[];
        onChange?: (level: T[]) => void;
      }
  );

/**
 * Tier-picker used by community-channel editor, post-editor DM
 * settings, post-editor subscribe-to-view, and clip access control.
 * Mirrors PopshopRN's `SubscriptionLevelPicker` minus RN-specific
 * styling props.
 *
 * Selecting a tier that the creator hasn't configured behaves in one
 * of two ways depending on whether `onConfigureSubscriptions` is
 * provided:
 *
 * - **With callback** (default in channel editor once subscription
 *   configuration page lands): shows a confirm dialog. On confirm,
 *   the host opens the subscription configuration UI; when that
 *   returns via `onDone()`, the picker refreshes and commits the
 *   value only if the tier became configured.
 * - **Without callback** (today, until the config page lands): the
 *   option renders disabled and clicking does nothing. Hosts pass a
 *   `disabled: true` on the option to make this visible.
 *
 * FOLLOWER and `ALL` always bypass the confirm flow.
 */
export function SubscriptionLevelPicker<T extends SubscriptionLevelBase>(
  props: Props<T>,
) {
  const {
    options,
    activeSubscriptions,
    onConfigureSubscriptions,
    refreshSubscriptions,
    disabled,
    className,
    itemClassName,
  } = props;

  const [pendingConfigureLevel, setPendingConfigureLevel] = useState<T | null>(
    null,
  );

  const configuredLevels = useMemo(() => {
    return new Set<string>(
      activeSubscriptions?.map(sub => sub.platform_sub_level as string),
    );
  }, [activeSubscriptions]);

  const isConfigured = (level: T): boolean => {
    if (
      level === PopStoreExtendedSubscriptionLevel.FOLLOWER ||
      level === PopStoreExtendedUserAccessLevel.ALL
    ) {
      return true;
    }
    return configuredLevels.has(level as unknown as string);
  };

  const commit = (level: T) => {
    if (props.multiple) {
      const current = props.value ?? [];
      const next = current.includes(level)
        ? current.filter(currentLevel => currentLevel !== level)
        : [...current, level];
      props.onChange?.(next);
    } else {
      props.onChange?.(level);
    }
  };

  const handleSelect = (level: T) => {
    if (disabled) return;

    if (isConfigured(level)) {
      commit(level);
      return;
    }

    if (!onConfigureSubscriptions) {
      // No CTA available — unconfigured tiers are inert.
      return;
    }

    setPendingConfigureLevel(level);
  };

  const handleConfirmConfigure = () => {
    if (!pendingConfigureLevel || !onConfigureSubscriptions) return;
    const level = pendingConfigureLevel;
    setPendingConfigureLevel(null);
    onConfigureSubscriptions({
      defaultSubscriptionLevel:
        level as unknown as PopStoreExtendedSubscriptionLevel,
      onDone: () => {
        void Promise.resolve(
          refreshSubscriptions?.({
            callback: (error, list) => {
              if (error != null) return;
              const nowConfigured = list.some(
                sub => sub.platform_sub_level === (level as unknown as string),
              );
              if (nowConfigured) commit(level);
            },
          }),
        );
      },
    });
  };

  const isSelected = (level: T): boolean => {
    if (props.multiple) {
      return (props.value ?? []).includes(level);
    }
    return props.value === level;
  };

  return (
    <>
      <div className={cn('grid grid-cols-2 gap-3', className)}>
        {options.map(option => {
          const optionDisabled =
            disabled === true ||
            option.disabled === true ||
            (!onConfigureSubscriptions && !isConfigured(option.value));
          const selected = isSelected(option.value);
          return (
            <button
              key={String(option.value)}
              type="button"
              onClick={() => handleSelect(option.value)}
              disabled={optionDisabled}
              aria-pressed={selected}
              className={cn(
                'flex h-auto min-h-[64px] flex-col items-center justify-center rounded-md border px-3 py-3 text-center transition',
                'disabled:cursor-not-allowed disabled:opacity-50',
                selected
                  ? 'border-brand bg-brand text-content-on-brand'
                  : 'border-line bg-surface-1 hover:border-checkout-neutral-500 text-content-1',
                itemClassName,
              )}
            >
              <span className="text-sm font-semibold">{option.title}</span>
              {option.subTitle ? (
                <span
                  className={cn(
                    'mt-0.5 text-[11px]',
                    option.subTitleVariant === 'danger'
                      ? 'text-red-400'
                      : selected
                        ? // Ink on a `brand` fill is `content-on-brand`, not
                          // `sink`. `sink` INVERTS — black in dark (which is why
                          // this matched develop's `text-black` and was never
                          // caught) and WHITE in light, so the selected tier's
                          // "and higher" was white-on-mint at 1.51:1.
                          // `content-on-brand/70` is 5.07:1 light / 6.60:1 dark.
                          'text-content-on-brand/70'
                        : 'text-content-3',
                  )}
                >
                  {option.subTitle}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <ConfirmDialog
        open={pendingConfigureLevel != null}
        onOpenChange={next => {
          if (!next) setPendingConfigureLevel(null);
        }}
        onConfirm={handleConfirmConfigure}
        title="Subscription Not Configured"
        description={
          pendingConfigureLevel
            ? `You have not configured subscription for ${pendingConfigureLevel}. Do you want to configure it now?`
            : ''
        }
        confirmLabel="Yes"
        cancelLabel="No"
      />
    </>
  );
}
